import { Link } from 'react-router-dom'
import PublicLayout from '../../components/layouts/PublicLayout'
import {
  DollarSign,
  FileCheck,
  Shield,
  AlertTriangle,
  CheckCircle,
  ArrowRight,
  Clock,
  FileText,
} from 'lucide-react'

const billingGates = [
  {
    gate: 'Locked Note Required',
    description: 'Claims can only be generated from clinical notes that are fully signed, attested, and locked.',
    blocked: 'Draft or unsigned notes',
  },
  {
    gate: 'Active Authorization',
    description: 'Authorization must be active (within date range, status = active) at time of service.',
    blocked: 'Expired or pending authorizations',
  },
  {
    gate: 'Available Units',
    description: 'Authorization must have sufficient units. System tracks real-time utilization.',
    blocked: 'Exceeding authorized units',
  },
  {
    gate: 'Service Code Match',
    description: 'Service code on session must match the authorization service code.',
    blocked: 'Mismatched service codes',
  },
]

const scrubberRules = [
  'Payer/member identity validation',
  'Provider NPI/taxonomy verification',
  'Place of service validation',
  'Modifier enforcement per payer rules',
  'ICD-10 diagnosis code validation',
  'Unit/time reconciliation',
  'Authorization reference verification',
  'Duplicate claim detection',
]

const ediCapabilities = [
  { code: '270/271', name: 'Eligibility Verification' },
  { code: '837P', name: 'Professional Claims' },
  { code: '999', name: 'Acknowledgements' },
  { code: '277CA', name: 'Claim Status' },
  { code: '835', name: 'ERA/Remittance' },
]

export default function BillingPage() {
  return (
    <PublicLayout>
      <div className="bg-gradient-to-b from-primary-50 to-white py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="max-w-2xl">
              <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
                Revenue Cycle Integrity
              </h1>
              <p className="mt-6 text-lg text-gray-600">
                Claims generated only from complete, locked documentation with valid authorization.
                Pre-submission scrubbing catches errors before they become denials.
              </p>
              <div className="mt-6 flex gap-3">
                <Link to="/contact" className="btn-primary">
                  Talk to billing
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </div>
            </div>
            <div className="overflow-hidden rounded-2xl shadow-2xl ring-1 ring-gray-200 bg-black">
              <video
                src="/videos/autism.mp4"
                className="w-full h-72 object-cover"
                autoPlay
                loop
                muted
                playsInline
                poster="/images/asd.png"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Billing Gates */}
      <div className="bg-white py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center mb-16">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900">
              Claim Generation Gates
            </h2>
            <p className="mt-4 text-gray-600">
              Every claim must pass these gates. No exceptions. No overrides.
            </p>
          </div>

          <div className="space-y-6">
            {billingGates.map((gate, index) => (
              <div
                key={gate.gate}
                className="bg-gray-50 rounded-lg p-6 flex items-start gap-6"
              >
                <div className="flex-shrink-0 w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center text-primary-700 font-bold text-lg">
                  {index + 1}
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 text-lg">{gate.gate}</h3>
                  <p className="mt-1 text-gray-600">{gate.description}</p>
                  <div className="mt-3 flex items-center gap-2 text-sm">
                    <span className="text-red-600 font-medium">BLOCKS:</span>
                    <span className="text-red-700">{gate.blocked}</span>
                  </div>
                </div>
                <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0" />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Claim Scrubber */}
      <div className="bg-gray-900 py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold tracking-tight text-white">
                Pre-Submission Claim Scrubber
              </h2>
              <p className="mt-4 text-gray-300">
                Every claim runs through the scrubber before submission.
                Errors block submission. Warnings flag for review.
              </p>

              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {scrubberRules.map((rule) => (
                  <div key={rule} className="flex items-center gap-2 text-gray-200">
                    <CheckCircle className="h-4 w-4 text-green-400" />
                    <span className="text-sm">{rule}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gray-800 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <FileCheck className="h-6 w-6 text-green-400" />
                <span className="font-semibold text-white">Scrubber Results</span>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-400">Status</span>
                  <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded">PASSED</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-400">Errors</span>
                  <span className="text-white">0</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-400">Warnings</span>
                  <span className="text-yellow-400">1</span>
                </div>
                <div className="pt-3 border-t border-gray-700">
                  <p className="text-xs text-yellow-400">
                    Warning: Timely filing deadline in 28 days
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* EDI Capabilities */}
      <div className="bg-white py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center mb-16">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900">
              EDI & Clearinghouse Integration
            </h2>
            <p className="mt-4 text-gray-600">
              Full electronic claims lifecycle support
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-4">
            {ediCapabilities.map((edi) => (
              <div
                key={edi.code}
                className="bg-gray-50 border border-gray-200 rounded-lg px-6 py-4 text-center"
              >
                <span className="block text-2xl font-bold text-primary-600">{edi.code}</span>
                <span className="text-sm text-gray-600">{edi.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Denial Management */}
      <div className="bg-gray-50 py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold tracking-tight text-gray-900">
                Denial & AR Management
              </h2>
              <p className="mt-4 text-gray-600">
                When denials happen, manage them efficiently with complete documentation.
              </p>

              <ul className="mt-8 space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                  <div>
                    <p className="font-medium text-gray-900">CARC/RARC Capture</p>
                    <p className="text-sm text-gray-600">Denial reason codes captured from ERA</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                  <div>
                    <p className="font-medium text-gray-900">Work Queues</p>
                    <p className="text-sm text-gray-600">Organized by payer, amount, and deadline</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                  <div>
                    <p className="font-medium text-gray-900">Appeal Packet Generator</p>
                    <p className="text-sm text-gray-600">One-click generation with all supporting docs</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                  <div>
                    <p className="font-medium text-gray-900">Recoupment Response</p>
                    <p className="text-sm text-gray-600">Workflow for responding to take-back requests</p>
                  </div>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-4">Denial Work Queue</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-red-50 rounded border-l-4 border-red-500">
                  <div>
                    <p className="font-medium text-sm">CLM-20240115-ABC123</p>
                    <p className="text-xs text-gray-500">CO-4: Code requires modifier</p>
                  </div>
                  <span className="text-red-600 font-medium">$245.00</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-yellow-50 rounded border-l-4 border-yellow-500">
                  <div>
                    <p className="font-medium text-sm">CLM-20240112-DEF456</p>
                    <p className="text-xs text-gray-500">CO-97: Benefit maximum reached</p>
                  </div>
                  <span className="text-yellow-600 font-medium">$180.00</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="bg-primary-600 py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold tracking-tight text-white">
            Clean Claims. Fewer Denials. Faster Payment.
          </h2>
          <p className="mt-4 text-lg text-primary-100">
            See how our billing integrity features protect your revenue.
          </p>
          <div className="mt-8">
            <Link to="/contact" className="btn bg-white text-primary-600 hover:bg-primary-50 text-base px-6 py-3">
              Request Demo
            </Link>
          </div>
        </div>
      </div>
    </PublicLayout>
  )
}
